import { useEffect, useRef } from 'react';
import ModalShell from '../overlays/ModalShell';
import { Z } from '../../constants/zIndex';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  details?: string;
  variant?: ConfirmDialogVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const VARIANT_CONFIG: Record<ConfirmDialogVariant, {
  icon: JSX.Element;
  iconBg: string;
  confirmBg: string;
  confirmHover: string;
  confirmText: string;
  accentBorder: string;
}> = {
  danger: {
    icon: (
      <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    ),
    iconBg: 'bg-red-500/10 ring-1 ring-red-500/20',
    confirmBg: 'bg-red-600 hover:bg-red-500',
    confirmHover: 'hover:shadow-red-900/40',
    confirmText: 'text-white',
    accentBorder: 'border-t-red-500',
  },
  warning: {
    icon: (
      <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    iconBg: 'bg-amber-500/10 ring-1 ring-amber-500/20',
    confirmBg: 'bg-amber-600 hover:bg-amber-500',
    confirmHover: 'hover:shadow-amber-900/40',
    confirmText: 'text-white',
    accentBorder: 'border-t-amber-500',
  },
  info: {
    icon: (
      <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    iconBg: 'bg-teal-500/10 ring-1 ring-teal-500/20',
    confirmBg: 'bg-teal-600 hover:bg-teal-500',
    confirmHover: 'hover:shadow-teal-900/40',
    confirmText: 'text-white',
    accentBorder: 'border-t-teal-500',
  },
};

export default function ConfirmDialog({
  open,
  title,
  message,
  details,
  variant = 'warning',
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cfg = VARIANT_CONFIG[variant];

  useEffect(() => {
    if (open) {
      setTimeout(() => cancelRef.current?.focus(), 80);
    }
  }, [open]);

  if (!open) return null;

  return (
    <ModalShell isOpen={open} onClose={onCancel} zIndex={Z.CONFIRM_DIALOG}>
      <div
        className={`w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl
          overflow-hidden border-t-2 ${cfg.accentBorder}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className={`shrink-0 w-11 h-11 rounded-xl ${cfg.iconBg} flex items-center justify-center`}>
              {cfg.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3
                id="confirm-dialog-title"
                className="text-[15px] font-semibold text-foreground leading-snug"
              >
                {title}
              </h3>
              <p className="mt-1.5 text-[13px] text-muted-foreground leading-relaxed">
                {message}
              </p>
              {details && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-muted border border-border">
                  <p className="text-[12px] text-foreground/80 font-mono leading-relaxed whitespace-pre-line">
                    {details}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-6 py-4 bg-muted/40 border-t border-border">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 text-[13px] font-medium text-muted-foreground hover:text-foreground
              bg-secondary hover:bg-accent border border-border rounded-lg
              transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className={`px-4 py-2 text-[13px] font-semibold ${cfg.confirmText} ${cfg.confirmBg}
              rounded-lg shadow-lg ${cfg.confirmHover} transition-all duration-150
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-current`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
